// İSG Portal — Sheet: sağdan açılan 480px panel (personel detayı; GL3'ten beri aynı zamanda
// FORM ÇEKMECESİ). `Modal.tsx`'in Radix `Dialog` sarmalayıcısını AYNALAR (odak tuzağı, Escape).
//
// **Katman (GL3, kararı sahip verdi).** Panel ve scrim `--z-drawer` (45) — `Modal`/`ConfirmDialog`
// (scrim + içerik `--z-modal` 60) çekmecenin ÜSTÜNDE açılır. Önceki tasarım (çekmece 65, modal
// 60) çekmece İÇİNDEN açılan onayı çekmecenin altında bırakıyordu. Çekmece içinde `--z-dropdown`
// (40) katmanındaki menü/tooltip çekmecenin ALTINDA kalır (bilinen sınır, tokens.css notu).
//
// **Footer, tile, busy (GL3).** `footer` verilirse gövde kayarken sabit kalan alt çubuk çıkar
// (Modal footer'ının AYNI Carbon grid'i). `tile` gövdeyi `data-tile` yapar: form alanları Modal
// içindeki ile AYNI Carbon dolgusunu alır ve açılışta ilk alana odaklanılır — OPT-IN, çünkü
// izin Sheet'lerindeki satır-içi Textarea'lar dolgu almamalı. `tile={false}` YALNIZ o iki
// izin Sheet'inin "eskisi gibi" kalması içindir; yeni bir form çekmecesi için geçerli bir seçim
// DEĞİLDİR, her form çekmecesi `tile` geçer (uzun vadede Modal gibi koşulsuz olması Carbon'a
// doğrudur). `busy` iken ✕ / Esc / dış tıklama
// kapanışı engellenir (gönderim yarıda bırakılamaz); "değişiklikler silinsin mi?" sorusu
// ÇAĞIRANIN işidir (`onOpenChange(false)` zaten çağıran filtresinden geçer) — Sheet yalnız
// `busy` kilidini taşır.
//
// **`restoreFocusRef` (S33 QA fix — HIGH).** Bu bileşen KASITLI OLARAK bir `Dialog.Trigger`
// RENDER ETMEZ (açan, çağıranın kendi liste satırındaki bir düğmedir — TEK bir sabit Trigger
// tüm satırları saramaz). Radix'in MODAL `Dialog.Content`'i (`@radix-ui/react-dialog` kaynağı,
// `DialogContentModal`) kapanışta odağı `previouslyFocusedElement`'e DEĞİL, `context.
// triggerRef.current`'e döndürür — bu ref YALNIZ gerçek bir `Dialog.Trigger` render edilirse
// dolar; burada HEP `null` kalır, yani odak sessizce `<body>`'ye düşer (canlı QA'da doğrulandı,
// Esc VE X ile). Çağıran, açan düğmenin kendi referansını `restoreFocusRef`'e YAZAR (bkz.
// `PermitPersonnelPanel.tsx`); biz `onCloseAutoFocus`'ta `preventDefault()` + `.focus()` ile
// Radix'in (burada işe yaramayan) varsayılanının YERİNİ alırız — `composeEventHandlers`
// `event.preventDefault()`sonrası kendi no-op'unu (`triggerRef.current?.focus()`) atlar.
// GL5: açıcı yoksa ya da DOM'dan kalktıysa odak `<body>`'ye düşmez, `#main-content` çapasına iner.

import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode, RefObject } from 'react';
import { useRef } from 'react';
import { FOCUSABLE_FIELD_SELECTOR, modalFooterClass } from './Modal';
import { cn } from './cn';

interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Kapat düğmesinin erişilebilir etiketi (i18n ile). */
  closeLabel?: string;
  /** Açan tetikleyici öğenin referansı — kapanışta odak buraya döner (dosya başlığı notu). */
  restoreFocusRef?: RefObject<HTMLElement | null>;
  /** Sabit alt aksiyon çubuğu. Verilmezse eski (footer'sız) görünüm. */
  footer?: ReactNode;
  /** Carbon footer eşit sütun sayısı (`Modal.footerColumns` ile aynı anlam). */
  footerColumns?: 1 | 2 | 3;
  /** Gövde Carbon "tile" + açılışta ilk alana odak (form çekmeceleri). */
  tile?: boolean;
  /** Gönderim sürüyor: ✕, Esc ve dış tıklama kapatmaz. */
  busy?: boolean;
  /** Esc'in Sheet'i kapatmasını çağıranın engellemesi için (ör. satır-içi editör): `event.preventDefault()`. */
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
}

export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  closeLabel = 'Kapat',
  restoreFocusRef,
  footer,
  footerColumns = 2,
  tile = false,
  busy = false,
  onEscapeKeyDown,
}: SheetProps) {
  const bodyRef = useRef<HTMLDivElement>(null);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        // Tek süzgeç: ✕, Esc ve overlay tıklaması hepsi buradan geçer.
        if (!next && busy) {
          return;
        }
        onOpenChange(next);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[var(--z-drawer)] bg-fg/40" />
        <Dialog.Content
          onEscapeKeyDown={onEscapeKeyDown}
          onOpenAutoFocus={(event) => {
            if (!tile) {
              return; // eski davranış: Radix varsayılanı
            }
            const firstField =
              bodyRef.current?.querySelector<HTMLElement>(FOCUSABLE_FIELD_SELECTOR);
            if (firstField) {
              event.preventDefault();
              firstField.focus();
            }
          }}
          onCloseAutoFocus={(event) => {
            const target = restoreFocusRef?.current;
            // Modal ile aynı güvence: açan düğüm DOM'dan kalktıysa Radix varsayılanına bırak.
            if (target && target !== document.body && document.contains(target)) {
              event.preventDefault();
              target.focus();
              return;
            }
            // GL5 — kullanılabilir bir açıcı yok (çekmece gezinmeyle açıldı ya da açıcı satır kalktı):
            // Radix odağı <body>'ye düşürürdü. Her iki AppLayout'un <main id="main-content" tabIndex={-1}> çapasına in.
            // Yalnız odak gerçekten kaybolduysa: bir host (ör. `useRowRemovalFocus` → "ekle" düğmesi) odağı
            // çekmece dışında canlı bir öğeye zaten koyduysa dokunma. Radix bu olayı FocusScope
            // kaldırılırken bir `setTimeout(0)` ile yollar → host'un efektleri çoktan koşmuştur ve
            // `activeElement` güncel değeri verir; çekmece hâlâ DOM'dayken (çıkış animasyonu) odak
            // içinde kalmış olabilir, o da "kayıp" sayılır.
            const active = document.activeElement;
            const focusLost =
              !active ||
              active === document.body ||
              (event.target instanceof Node && event.target.contains(active));
            const main = document.getElementById('main-content');
            if (focusLost && main) {
              event.preventDefault();
              main.focus({ preventScroll: true });
            }
          }}
          className={cn(
            'fixed inset-y-0 right-0 z-[var(--z-drawer)] flex w-full max-w-[480px] flex-col',
            'border-l border-border bg-surface shadow-lg focus:outline-none',
            'carbon:rounded-none carbon:shadow-none',
          )}
        >
          <div className="flex shrink-0 items-start justify-between gap-4 border-b border-border p-5 pb-4 carbon:p-4 carbon:pr-12">
            <div className="flex flex-col gap-1">
              <Dialog.Title className="text-lg font-semibold text-fg carbon:text-xl carbon:font-normal carbon:leading-7">
                {title}
              </Dialog.Title>
              {description ? (
                <Dialog.Description className="text-sm text-muted">
                  {description}
                </Dialog.Description>
              ) : null}
            </div>
            <Dialog.Close
              disabled={busy}
              className={cn(
                'rounded-md p-1 text-muted hover:bg-surface-muted hover:text-fg',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                'disabled:cursor-not-allowed disabled:opacity-50',
                'carbon:absolute carbon:right-0 carbon:top-0 carbon:h-12 carbon:w-12 carbon:rounded-none carbon:p-0',
              )}
              aria-label={closeLabel}
            >
              <X className="mx-auto h-5 w-5" aria-hidden="true" />
            </Dialog.Close>
          </div>
          <div
            ref={bodyRef}
            data-tile={tile ? '' : undefined}
            className="min-h-0 flex-1 overflow-y-auto p-5 carbon:p-4"
          >
            {children}
          </div>
          {footer ? (
            // Modal ile AYNI footer sınıfı (`modalFooterClass`) — kopya yok. Carbon'da hücre
            // `min-h-16`: DE/EN uzun etiket sarılırsa hücre büyür, taşmaz.
            <div data-modal-footer className={modalFooterClass(footerColumns)}>
              {footer}
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
